import { RouteComponentProps } from "@reach/router"
import { DocumentTitleOverride, StatusBar, usePaginatedApi } from "@shared/primitive-components"
import { toSortOrder } from "@shared/platform-api/requests"
import { useQueryStringWithStateHook } from "@shared/utils/helpers/useQueryStringWithStateHooks"
import { ListWrapperWithContext } from "../../components/ListWrapperWithContext"
import { usePolicyAndFleet } from "../../contexts"
import { usePolicyAccessCapabilities } from "../../hooks"
import { LowConnectivityBanner, PrevPolicyDropdown, SummaryChartCard } from "../components"
import { MileageChartComponent, MileageFleetListTable, MileageSummaryComponent } from "../components/MileageComponents"
import {
  FleetEntryWithMileage,
  FleetEntriesMileageSortKeys,
  fleetEntriesMileageKeysMap,
  SearchVehiclesInput,
  useSearchVehicles,
} from "../../api/rebates/useGetFleetEntriesWithMileage"

export const MILEAGE_DTI = {
  TITLE: `mileage_data_title`,
  SUBTITLE: `mileage_data_sub_title`,
  SUMMARY_CHART_CARD: `mileage_summary_chart_card`,
  METRICS_STATUS: `mileage_metrics_status`,
}

export const Mileage: React.FC<RouteComponentProps> = () => {
  const { policy, connectivity } = usePolicyAndFleet()
  const { isRebatePolicy } = usePolicyAccessCapabilities()
  const [limit, setLimit] = useQueryStringWithStateHook(`limit`)
  const [sortOrder, setSortOrder] = useQueryStringWithStateHook(`sort_order`)
  const [sortKey, setSortKey] = useQueryStringWithStateHook(`sort_key`)
  const [searchTerm, setSearchTerm] = useQueryStringWithStateHook(`term`)

  const queryStringAndStateSetters: Record<string, (newValue: string) => void> = {
    setLimit,
    setSortOrder,
    setSortKey,
    setSearchTerm,
  }

  const searchInput: SearchVehiclesInput = {
    id: policy.id,
    connectedVrns: connectivity?.data?.connectedVrns || [],
    limit: limit && /^[0-9]*$/.test(limit) ? parseInt(limit, 10) : 50,
    sortKey: (sortKey || `vrn`) as FleetEntriesMileageSortKeys,
    sortOrder: toSortOrder(sortOrder),
    term: searchTerm || ``,
  }

  const [res, reload] = useSearchVehicles(searchInput)

  const { page, dataCount, sort, setSort, pagination, search } = usePaginatedApi<
    FleetEntryWithMileage,
    SearchVehiclesInput,
    FleetEntriesMileageSortKeys,
    FleetEntriesMileageSortKeys
  >(searchInput, reload, res.result, fleetEntriesMileageKeysMap, queryStringAndStateSetters)

  const AboveTable = isRebatePolicy ? (
    <>
      <SummaryChartCard
        dataTestId={MILEAGE_DTI.SUMMARY_CHART_CARD}
        summary={MileageSummaryComponent}
        chart={MileageChartComponent}
      />
      <StatusBar
        dataTestId={MILEAGE_DTI.METRICS_STATUS}
        type="error"
        placement="belowTitle"
        message="These metrics are updated on a monthly basis starting from the day your policy began."
      />
    </>
  ) : null

  const BelowTable = (
    <StatusBar
      type="info"
      placement="bottom"
      message="*For disconnected vehicles, mileage is calculated by assuming all estimated miles have been driven."
    />
  )

  return (
    <>
      {!!(connectivity.data || policy.connectivityRatio) && <LowConnectivityBanner policy={policy} />}
      <DocumentTitleOverride>Flock Motor | Mileage</DocumentTitleOverride>
      <ListWrapperWithContext
        aboveTable={AboveTable}
        belowTable={BelowTable}
        dataCount={dataCount}
        subTitle="Mileage Per Vehicle"
        title="Mileage"
        dataTestIds={{
          title: MILEAGE_DTI.TITLE,
          subTitle: MILEAGE_DTI.SUBTITLE,
        }}
        listApiStatus={{
          loading: !!res.loading,
        }}
        search={{
          ...search,
          placeholderText: `Search by VRN`,
        }}
        PreviousPolicyDropdown={<PrevPolicyDropdown />}
      >
        <MileageFleetListTable
          page={page}
          sort={sort}
          dataCount={dataCount}
          sortData={setSort}
          pagination={pagination}
        />
      </ListWrapperWithContext>
    </>
  )
}
